import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, KeyRound, CheckCircle, AlertCircle } from 'lucide-react';
import PasswordStrengthIndicator, { checkPasswordStrength } from '../components/PasswordStrengthIndicator';
import API from '../api';

export default function ActivatePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [state, setState] = useState('loading'); // loading | valid | invalid | success
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    API.get(`/api/auth/activate/check?token=${encodeURIComponent(token)}`)
      .then(res => { setUserInfo(res.data); setState('valid'); })
      .catch(() => setState('invalid'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    const strength = checkPasswordStrength(password);
    if (strength.level < 3) { toast.error('Le mot de passe ne respecte pas tous les critères'); return; }

    setSaving(true);
    try {
      await API.post('/api/auth/activate', { token, password });
      setState('success');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1A3A6E 50%, #1e3a8a 100%)' }}>
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.15)' }}>
            <KeyRound size={20} className="text-white" />
          </div>
          <span className="text-2xl font-bold text-white" style={{  }}>
            Bedee
          </span>
        </div>

        <div className="rounded-2xl shadow-2xl overflow-hidden" style={{ background: 'white' }}>
          {/* Loading */}
          {state === 'loading' && (
            <div className="px-8 py-12 flex flex-col items-center gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Vérification du lien...</p>
            </div>
          )}

          {/* Lien invalide / expiré */}
          {state === 'invalid' && (
            <div className="px-8 py-12 text-center">
              <AlertCircle size={40} className="mx-auto mb-4" style={{ color: '#EF4444' }} />
              <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                Lien invalide ou expiré
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                Ce lien d'activation n'est plus valable. Demandez à votre administrateur de renvoyer l'invitation.
              </p>
              <button onClick={() => navigate('/login')}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white"
                style={{ background: 'var(--primary-gradient)' }}>
                Retour à la connexion
              </button>
            </div>
          )}

          {/* Succès */}
          {state === 'success' && (
            <div className="px-8 py-12 text-center">
              <CheckCircle size={40} className="mx-auto mb-4" style={{ color: '#22C55E' }} />
              <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                Compte activé !
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                Votre mot de passe a été créé. Vous pouvez maintenant vous connecter.
              </p>
              <button onClick={() => navigate('/login')}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white"
                style={{ background: 'var(--primary-gradient)' }}>
                Se connecter
              </button>
            </div>
          )}

          {/* Formulaire de création */}
          {state === 'valid' && (
            <form onSubmit={handleSubmit} className="px-8 py-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold" style={{  color: 'var(--text-primary)' }}>
                  Créez votre mot de passe
                </h2>
                {userInfo && (
                  <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                    Bienvenue, <strong>{userInfo.name}</strong> — {userInfo.email}
                  </p>
                )}
              </div>

              {/* Mot de passe */}
              <div className="mb-4">
                <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
                  style={{ color: 'var(--text-muted)' }}>Mot de passe</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    data-testid="activate-password-input"
                    required autoFocus
                    className="w-full px-3 py-2.5 pr-10 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
                    style={{ borderColor: 'var(--border-light)', color: 'var(--text-primary)' }}
                    placeholder="Choisissez un mot de passe fort"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--text-muted)' }}>
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <PasswordStrengthIndicator password={password} />
              </div>

              {/* Confirmation */}
              <div className="mb-6">
                <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
                  style={{ color: 'var(--text-muted)' }}>Confirmer le mot de passe</label>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  data-testid="activate-confirm-input"
                  required
                  className="w-full px-3 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
                  style={{
                    borderColor: confirm && confirm !== password ? '#EF4444' : 'var(--border-light)',
                    color: 'var(--text-primary)'
                  }}
                  placeholder="Répétez le mot de passe"
                />
                {confirm && confirm !== password && (
                  <p className="text-xs mt-1" style={{ color: '#EF4444' }}>Les mots de passe ne correspondent pas</p>
                )}
              </div>

              <button type="submit"
                disabled={saving || !password || !confirm || password !== confirm || checkPasswordStrength(password).level < 3}
                data-testid="activate-submit-btn"
                className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-opacity"
                style={{ background: 'var(--primary-gradient)' }}>
                {saving ? 'Activation...' : 'Activer mon compte'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'rgba(255,255,255,0.4)' }}>
          © {new Date().getFullYear()} Bedee — Gestion de parc matériel
        </p>
      </div>
    </div>
  );
}
